import {createSlice} from '@reduxjs/toolkit'
import {INoteFE} from 'interfaces/INoteFE'
import {
  AddAttachmentNoteToList,
  createAddToNoteList,
  deleteNoteListItemFileAttachement,
  deleteNotListItem,
  fetchNoteListForEntity,
  fetchNoteListForProject,
  updateNoteListItem,
} from 'redux/actions/noteListActions'

//projects can have notes
//entities can have notes
//key for the note list is a composite of entityType|enttyId OR projectId for now
//status is provided for overall library of all lists and for each list by key AND for each note by key and noteId
//The extraReducers in this slice may seem like a lot but it's just repeated logic to track statuses for a note, it's attachemnts and all the separate
//entities that can have notes.
//so simply put a key = <entityType|entityId> where the pipe is included in the previous ...or <projectId> as primary key for the note list, then
//it's that <key|noteId> to track individual note status (needed when deleting or adding an attachment to a note)

//then ordering of notes i not done by the api fetch, so we add an orderNoteList function to order the notes by their
//updated or created dates which can come from multiple fileds returned from the api, sometimes we get created_at, other times we get created as an object
//so logic here handles that.

interface IReduxNote {
  key: string
  notes: INoteFE[]
}
interface IkeyStatus {
  key: string
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
}

export interface NoteSliceState {
  noteLists: IReduxNote[] | undefined
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  keyListStatus: IkeyStatus[]
  keyNoteItemStatus: IkeyStatus[] //takes a key and noteId
  error: string | null
}

const initialState: NoteSliceState = {
  noteLists: undefined,
  status: 'idle',
  keyListStatus: [],
  keyNoteItemStatus: [],
  error: null,
}

const orderNoteList = (notes: INoteFE[]) => {
  return notes.sort((a, b) => {
    return (
      new Date(
        b.updated_at
          ? b.updated_at
          : b.updated?.at
          ? b.updated?.at
          : b.created_at
          ? b.created_at
          : b.created?.at
          ? b.created.at
          : ''
      ).getTime() -
      new Date(
        a.updated_at
          ? a.updated_at
          : a.updated?.at
          ? a.updated?.at
          : a.created_at
          ? a.created_at
          : a.created?.at
          ? a.created.at
          : ''
      ).getTime()
    )
  })
}

const noteListSlice = createSlice({
  name: 'noteList',
  initialState,
  reducers: {
    clearNoteList: (state) => {
      state.noteLists = undefined
    },
    updateNotesList: (state, action) => {
      state.noteLists = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNoteListForEntity.pending, (state, action) => {
        state.status = 'loading'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter(
            (x) => x.key !== action?.meta.arg.entityType + '|' + action?.meta.arg.entityId
          ) || []),
          {key: action?.meta.arg.entityType + '|' + action?.meta.arg.entityId, status: 'loading'},
        ]
      })
      .addCase(fetchNoteListForEntity.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.payload?.key) || []),
          {key: action?.payload?.key ?? '', status: 'succeeded'},
        ]
        state.noteLists = [
          ...(state.noteLists?.filter((x) => x.key !== action?.payload?.key) || []),
          {key: action?.payload?.key ?? '', notes: orderNoteList(action?.payload?.notes ?? [])},
        ]
      })
      .addCase(fetchNoteListForEntity.rejected, (state, action) => {
        state.status = 'failed'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter(
            (x) => x.key !== action?.meta.arg.entityType + '|' + action?.meta.arg.entityId
          ) || []),
          {key: action?.meta.arg.entityType + '|' + action?.meta.arg.entityId, status: 'failed'},
        ]
        state.error = action.payload as string
      })
      .addCase(fetchNoteListForProject.pending, (state, action) => {
        state.status = 'loading'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.project_id) || []),
          {key: action?.meta.arg.project_id, status: 'loading'},
        ]
      })
      .addCase(fetchNoteListForProject.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.payload?.key) || []),
          {key: action?.payload?.key ?? '', status: 'succeeded'},
        ]
        state.noteLists = [
          ...(state.noteLists?.filter((x) => x.key !== action?.payload?.key) || []),
          {key: action?.payload?.key ?? '', notes: orderNoteList(action?.payload?.notes ?? [])},
        ]
      })
      .addCase(fetchNoteListForProject.rejected, (state, action) => {
        state.status = 'failed'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.project_id) || []),
          {key: action?.meta.arg.project_id, status: 'failed'},
        ]
        state.error = action.payload as string
      })
      .addCase(createAddToNoteList.pending, (state, action) => {
        state.status = 'loading'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter(
            (x) => x.key !== action?.meta.arg.entityType + '|' + action?.meta.arg.entityId
          ) || []),
          {key: action?.meta.arg.entityType + '|' + action?.meta.arg.entityId, status: 'loading'},
        ]
      })
      .addCase(createAddToNoteList.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.payload?.key) || []),
          {key: action?.payload?.key ?? '', status: 'succeeded'},
        ]
        const found = state.noteLists?.find((f) => f.key === action?.payload?.key)
        if (found && action?.payload?.note) {
          found.notes.push(action?.payload?.note)
          if (state.noteLists) {
            const index = state.noteLists.findIndex((f) => f.key === action?.payload?.key)
            if (index !== -1) {
              found.notes = orderNoteList(found.notes)
              state.noteLists[index] = found
            }
          }
        }
      })
      .addCase(createAddToNoteList.rejected, (state, action) => {
        state.status = 'failed'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter(
            (x) => x.key !== action?.meta.arg.entityType + '|' + action?.meta.arg.entityId
          ) || []),
          {key: action?.meta.arg.entityType + '|' + action?.meta.arg.entityId, status: 'failed'},
        ]
        state.error = action.payload as string
      })
      .addCase(updateNoteListItem.pending, (state, action) => {
        state.status = 'loading'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'loading'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.noteId
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.noteId, status: 'loading'},
        ]
      })
      .addCase(updateNoteListItem.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.payload?.key) || []),
          {key: action?.payload?.key ?? '', status: 'succeeded'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.payload?.key + '|' + action?.payload?.noteId
          ) || []),
          {key: action?.payload?.key + '|' + action?.payload?.noteId, status: 'succeeded'},
        ]
        //find the list by key
        const found = state.noteLists?.find((f) => f.key === action?.payload?.key)
        if (found && action?.payload?.note) {
          //find the note by id
          const index = found.notes.findIndex((f) => f.id === action?.payload?.noteId)
          if (index !== -1) {
            //update the note
            found.notes[index] = action?.payload?.note
          }
          //use the updated found to update the note list
          if (state.noteLists) {
            const index = state.noteLists.findIndex((f) => f.key === action?.payload?.key)
            if (index !== -1) {
              found.notes = orderNoteList(found.notes)
              state.noteLists[index] = found
            }
          }
        }
      })
      .addCase(updateNoteListItem.rejected, (state, action) => {
        state.status = 'failed'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'failed'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.noteId
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.noteId, status: 'failed'},
        ]
        state.error = action.payload as string
      })
      .addCase(deleteNotListItem.pending, (state, action) => {
        state.status = 'loading'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'loading'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.noteId
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.noteId, status: 'loading'},
        ]
      })
      .addCase(deleteNotListItem.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'succeeded'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.noteId
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.noteId, status: 'succeeded'},
        ]
        //find the list by key
        const found = state.noteLists?.find((f) => f.key === action?.meta.arg.key)
        if (found) {
          //find the note by id
          const index = found.notes.findIndex((f) => f.id === action?.meta.arg.noteId)
          if (index !== -1) {
            //delete the note
            found.notes.splice(index, 1)
          }
          //use the updated found to update the note list
          if (state.noteLists) {
            const index = state.noteLists.findIndex((f) => f.key === action?.meta.arg.key)
            if (index !== -1) {
              found.notes = orderNoteList(found.notes)
              state.noteLists[index] = found
            }
          }
        }
      })
      .addCase(deleteNotListItem.rejected, (state, action) => {
        state.status = 'failed'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'failed'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.noteId
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.noteId, status: 'failed'},
        ]
        state.error = action.payload as string
      })
      .addCase(deleteNoteListItemFileAttachement.pending, (state, action) => {
        state.status = 'loading'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'loading'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.note.id
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.note.id, status: 'loading'},
        ]
      })
      .addCase(deleteNoteListItemFileAttachement.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'succeeded'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.note.id
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.note.id, status: 'succeeded'},
        ]
        //find the list by key
        const found = state.noteLists?.find((f) => f.key === action?.meta.arg.key)
        if (found) {
          //find the note by id
          const index = found.notes.findIndex((f) => f.id === action?.payload?.noteId)
          if (index !== -1) {
            //update the note
            if (action?.payload?.deletedNote) {
              found.notes[index] = action.payload.deletedNote
            }
          }
          //use the updated found to update the note list
          if (state.noteLists) {
            const index = state.noteLists.findIndex((f) => f.key === action?.meta.arg.key)
            if (index !== -1) {
              found.notes = orderNoteList(found.notes)
              state.noteLists[index] = found
            }
          }
        }
      })
      .addCase(deleteNoteListItemFileAttachement.rejected, (state, action) => {
        state.status = 'failed'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.meta.arg.key) || []),
          {key: action?.meta.arg.key, status: 'failed'},
        ]
        state.keyNoteItemStatus = [
          ...(state.keyNoteItemStatus?.filter(
            (x) => x.key !== action?.meta.arg.key + '|' + action?.meta.arg.note.id
          ) || []),
          {key: action?.meta.arg.key + '|' + action?.meta.arg.note.id, status: 'failed'},
        ]
        state.error = action.payload as string
      })
      .addCase(AddAttachmentNoteToList.pending, (state, action) => {
        state.status = 'loading'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter(
            (x) => x.key !== action?.meta.arg.entityType + '|' + action?.meta.arg.entityId
          ) || []),
          {key: action?.meta.arg.entityType + '|' + action?.meta.arg.entityId, status: 'loading'},
        ]
      })
      .addCase(AddAttachmentNoteToList.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter((x) => x.key !== action?.payload?.key) || []),
          {key: action?.payload?.key ?? '', status: 'succeeded'},
        ]
        if (action.payload?.isUpdate) {
          //if it's an update to a note, we find the note
          const found = state.noteLists?.find((f) => f.key === action?.payload?.key)

          if (found && action?.payload?.note) {
            const index = found.notes.findIndex((f) => f.id === action?.payload?.note?.id)
            if (index !== -1) {
              //replace the note fo the found list
              found.notes[index] = action?.payload?.note
            }
            if (state.noteLists) {
              const index = state.noteLists.findIndex((f) => f.key === action?.payload?.key)
              if (index !== -1) {
                found.notes = orderNoteList(found.notes)
                state.noteLists[index] = found
              }
            }
          }
        } else {
          //find the notelist
          const found = state.noteLists?.find((f) => f.key === action?.payload?.key)
          if (found && action?.payload?.note) {
            //push on the new note
            found.notes.push(action?.payload?.note)
            if (state.noteLists) {
              //replace arra with the new found one
              const index = state.noteLists.findIndex((f) => f.key === action?.payload?.key)
              if (index !== -1) {
                found.notes = orderNoteList(found.notes)
                state.noteLists[index] = found
              }
            }
          }
        }
      })
      .addCase(AddAttachmentNoteToList.rejected, (state, action) => {
        state.status = 'failed'
        state.keyListStatus = [
          ...(state.keyListStatus?.filter(
            (x) => x.key !== action?.meta.arg.entityType + '|' + action?.meta.arg.entityId
          ) || []),
          {key: action?.meta.arg.entityType + '|' + action?.meta.arg.entityId, status: 'failed'},
        ]
        state.error = action.payload as string
      })
  },
})

export const {clearNoteList, updateNotesList} = noteListSlice.actions
export default noteListSlice.reducer
